import {
  GraphGroup,
  GraphNode,
  RawGraphNode2,
  createGraphItem,
  isLinkToGroupingAncestor,
} from '@ardoq/graph';
import { HierarchyOfLinkedComponentsWithDirection } from '@ardoq/data-model';
import {
  ComponentHierarchySortComparator,
  RawGraphItemsSortComparator,
  TimelineViewGraphHierarchy,
  TimelineViewModelState,
} from '../types';
import { groupBy, isEqual, partition } from 'lodash';
import { ArdoqId, GroupType } from '@ardoq/api-types';
import {
  ComponentByIdSortDependencies,
  compareComponentsById,
  orderUnsetValues,
  applySortOrder,
  comparePrimitiveValues,
} from '@ardoq/common-helpers';
import { localeCompareNumericLowercase } from '@ardoq/locale';

const collectChildren = (nodes: RawGraphNode2[]) =>
  nodes.flatMap(({ children }) => children ?? []);

const mergeGroupingDuplicates = (duplicates: RawGraphNode2[]): RawGraphNode2 =>
  duplicates.length === 1
    ? duplicates[0]
    : { ...duplicates[0], children: collectChildren(duplicates) };

/**
 * Traversal nodes are deduplicated when they are built, so merging only needs to
 * deduplicate again where the children of several duplicates meet.
 */
const mergeTraversalDuplicates = (
  duplicates: RawGraphNode2[]
): RawGraphNode2 =>
  duplicates.length === 1
    ? duplicates[0]
    : {
        ...duplicates[0],
        children: Object.values(
          groupBy(collectChildren(duplicates), 'modelId')
        ).map(mergeTraversalDuplicates),
      };

type HierarchyMergeContext = {
  traversalRootsByModelId: Map<
    ArdoqId,
    HierarchyOfLinkedComponentsWithDirection
  >;
  usedTraversalRoots: Set<string>;
  groupingAncestorIds: ReadonlySet<ArdoqId>;
  omitLinksToGroupingAncestors: boolean;
};

type ToTraversalChildrenArgs = {
  traversalRoot: HierarchyOfLinkedComponentsWithDirection;
  groupingAncestorIds: ReadonlySet<ArdoqId>;
  omitLinksToGroupingAncestors: boolean;
};
const toTraversalChildren = ({
  traversalRoot,
  groupingAncestorIds,
  omitLinksToGroupingAncestors,
}: ToTraversalChildrenArgs): RawGraphNode2[] =>
  traversalRoot.linkedComponents
    .filter(
      linkedComponent =>
        !omitLinksToGroupingAncestors ||
        !isLinkToGroupingAncestor(linkedComponent, groupingAncestorIds)
    )
    .map(hierarchyOfLinkedComponentsToRawGraphItem);

/**
 * Timeline is different from dependency map in a sense that currently all nodes are looking the
 * same in the view. We cannot tell which one is a linḱed component and which one is a parent.
 * Therefore, we deduplicate the nodes with the same modelId in the same parent scope. A grouping
 * node absorbs the children of traversal nodes it deduplicates.
 *
 * Only grouping nodes are expanded by their traversal root. Traversal nodes already carry their
 * linked components, expanding them would make the traversal hierarchy extend itself.
 */
const mergeTraversalHierarchyIntoGroupingNode = (
  context: HierarchyMergeContext,
  groupingNode: RawGraphNode2,
  absorbedTraversalChildren: RawGraphNode2[] = []
): RawGraphNode2 => {
  const {
    traversalRootsByModelId,
    usedTraversalRoots,
    groupingAncestorIds,
    omitLinksToGroupingAncestors,
  } = context;

  const modelId = groupingNode.modelId;

  const traversalRoot =
    modelId && !usedTraversalRoots.has(modelId)
      ? traversalRootsByModelId.get(modelId)
      : undefined;

  if (traversalRoot) {
    usedTraversalRoots.add(traversalRoot.componentId);
  }

  const traversalChildren = traversalRoot
    ? absorbedTraversalChildren.concat(
        toTraversalChildren({
          traversalRoot,
          groupingAncestorIds,
          omitLinksToGroupingAncestors,
        })
      )
    : absorbedTraversalChildren;

  const childContext = {
    ...context,
    groupingAncestorIds: modelId
      ? new Set([...groupingAncestorIds, modelId])
      : groupingAncestorIds,
  };

  const groupingChildren = groupingNode.children ?? [];
  const groupingChildSet = new Set(groupingChildren);

  const children = Object.values(
    groupBy(groupingChildren.concat(traversalChildren), 'modelId')
  ).map(duplicates => {
    const [groupingDuplicates, traversalDuplicates] = partition(
      duplicates,
      duplicate => groupingChildSet.has(duplicate)
    );

    return groupingDuplicates.length
      ? mergeTraversalHierarchyIntoGroupingNode(
          childContext,
          mergeGroupingDuplicates(groupingDuplicates),
          collectChildren(traversalDuplicates)
        )
      : mergeTraversalDuplicates(traversalDuplicates);
  });

  return { ...groupingNode, children };
};

const hierarchyOfLinkedComponentsToRawGraphItem = (
  hierarchyItem: HierarchyOfLinkedComponentsWithDirection
): RawGraphNode2 => ({
  id: hierarchyItem.componentId,
  modelId: hierarchyItem.componentId,
  children: Object.values(
    groupBy(
      hierarchyItem.linkedComponents.map(
        hierarchyOfLinkedComponentsToRawGraphItem
      ),
      'modelId'
    )
  ).map(mergeTraversalDuplicates),
});

type GetCommonTraversalAndGroupingHierarchyArgs = {
  graphAsHierarchy: Record<string, HierarchyOfLinkedComponentsWithDirection>;
  rootItems: RawGraphNode2[];
  omitLinksToGroupingAncestors: boolean;
};
const getCommonTraversalAndGroupingHierarchy = ({
  graphAsHierarchy,
  rootItems,
  omitLinksToGroupingAncestors,
}: GetCommonTraversalAndGroupingHierarchyArgs) => {
  // Hierarchy of linked components is defined by the traversal
  const traversalRootsByModelId = new Map(
    Object.values(graphAsHierarchy).map(traversalRoot => [
      traversalRoot.componentId,
      traversalRoot,
    ])
  );

  const context: HierarchyMergeContext = {
    traversalRootsByModelId,
    usedTraversalRoots: new Set<string>(),
    groupingAncestorIds: new Set<ArdoqId>(),
    omitLinksToGroupingAncestors,
  };

  return rootItems.map(rootItem =>
    mergeTraversalHierarchyIntoGroupingNode(context, rootItem)
  );
};

const getGraphNodeOrGroupForRawItem = (
  rawGraphItem: RawGraphNode2,
  usedNodes: Map<string, GraphGroup | GraphNode>,
  parentGraphNodeId?: string
) => {
  const usedNodesKey = `${rawGraphItem.id}-${parentGraphNodeId || ''}`;

  const nodeCreatedByParent = usedNodes.get(usedNodesKey);

  const currentGraphNode =
    nodeCreatedByParent ??
    createGraphItem({ item: rawGraphItem, useUniqueId: true });

  if (!nodeCreatedByParent) {
    usedNodes.set(usedNodesKey, currentGraphNode);
  }

  return currentGraphNode;
};

type CreateViewGraphHierarchyState = {
  usedNodes: Map<string, GraphGroup | GraphNode>;
  viewGraphHierarchy: TimelineViewGraphHierarchy;
};
export const createViewGraphHierarchy =
  (parentGraphNodeId?: string) =>
  (
    reducerState: CreateViewGraphHierarchyState,
    rawGraphItem: RawGraphNode2
  ): CreateViewGraphHierarchyState => {
    const { viewGraphHierarchy, usedNodes } = reducerState;

    const currentGraphNode = getGraphNodeOrGroupForRawItem(
      rawGraphItem,
      usedNodes,
      parentGraphNodeId
    );

    const rawItemChildren = rawGraphItem.children || [];

    const childGraphNodes = rawItemChildren.map(childItem =>
      getGraphNodeOrGroupForRawItem(childItem, usedNodes, currentGraphNode.id)
    );

    viewGraphHierarchy.set(currentGraphNode.id, {
      children: childGraphNodes,
      graphItem: currentGraphNode,
      rawGraphItem,
      parentGraphNodeId,
    });

    return rawItemChildren.reduce(
      createViewGraphHierarchy(currentGraphNode.id),
      {
        viewGraphHierarchy,
        usedNodes,
      }
    );
  };

type GetViewGraphHierarchyFromTraversal = (args: {
  graphAsHierarchy: Record<string, HierarchyOfLinkedComponentsWithDirection>;
  rootItems: RawGraphNode2[];
  omitLinksToGroupingAncestors: boolean;
}) => TimelineViewGraphHierarchy;
export const getViewGraphHierarchyFromTraversal: GetViewGraphHierarchyFromTraversal =
  ({ graphAsHierarchy, rootItems, omitLinksToGroupingAncestors }) => {
    const mergedHierarchy = getCommonTraversalAndGroupingHierarchy({
      graphAsHierarchy,
      rootItems,
      omitLinksToGroupingAncestors,
    });

    const { viewGraphHierarchy } = mergedHierarchy.reduce(
      createViewGraphHierarchy(),
      {
        viewGraphHierarchy: new Map(),
        usedNodes: new Map(),
      }
    );

    return viewGraphHierarchy;
  };

type CompareGraphItemsByComponentIdArgs = {
  componentIdA?: string | null;
  componentIdB?: string | null;
  sortDependencies: ComponentByIdSortDependencies;
};
const compareGraphItemsByComponentId = ({
  componentIdA,
  componentIdB,
  sortDependencies,
}: CompareGraphItemsByComponentIdArgs) => {
  const aIsNil =
    !componentIdA || !sortDependencies.componentAttributesById[componentIdA];
  const bIsNil =
    !componentIdB || !sortDependencies.componentAttributesById[componentIdB];

  return aIsNil || bIsNil
    ? orderUnsetValues({
        aIsNil,
        bIsNil,
        orderUnsetLast: sortDependencies.sort.orderUnsetLast,
      })
    : compareComponentsById(componentIdA, componentIdB, sortDependencies);
};

type Group = {
  id: string;
  type: GroupType;
  label: string;
  fieldGroupValue?: any;
};
const isGroup = (rawGraphItem: RawGraphNode2): rawGraphItem is Group =>
  rawGraphItem.type !== undefined &&
  Object.values(GroupType).includes(rawGraphItem.type) &&
  rawGraphItem.label !== undefined;

type WorkspaceGroup = Group & {
  type: GroupType.WORKSPACE;
  modelId: ArdoqId;
};
const isWorkspaceGroup = (
  rawGraphItem: RawGraphNode2
): rawGraphItem is WorkspaceGroup =>
  isGroup(rawGraphItem) && rawGraphItem.type === GroupType.WORKSPACE;

type FieldGroup = Group & {
  type: GroupType.FIELD;
  fieldGroupValue: any;
};

const isFieldGroup = (
  rawGraphItem: RawGraphNode2
): rawGraphItem is FieldGroup =>
  isGroup(rawGraphItem) &&
  rawGraphItem.type === GroupType.FIELD &&
  rawGraphItem.fieldGroupValue !== undefined;

/**
 * Raw Graph Items aren't always components, but unless they are workapse groups, we
 * need to sort them. To not change the structure of the graph, we only sort if both
 * items are groups.
 *
 * We don't sort workspace groups in views, because their order should correspond to
 * the order of the source data, until required otherwise.
 */
const sortRawGraphItemNonComponentGroups = (
  itemA: RawGraphNode2,
  itemB: RawGraphNode2,
  sortDependencies: ComponentByIdSortDependencies
) => {
  if (
    !(isGroup(itemA) && isGroup(itemB)) ||
    isWorkspaceGroup(itemA) ||
    isWorkspaceGroup(itemB)
  ) {
    return 0; // No sorting for workspace groups
  }

  if (isFieldGroup(itemA) && isFieldGroup(itemB)) {
    /**
     * If it is a field group, sort by field value of their children.
     *
     * We don't group by date range fields, only by date fields.
     */

    const { sort, userLocale } = sortDependencies;

    const rawValueA = itemA.fieldGroupValue;
    const rawValueB = itemB.fieldGroupValue;

    const valueA = rawValueA instanceof Date ? rawValueA.valueOf() : rawValueA;
    const valueB = rawValueB instanceof Date ? rawValueB.valueOf() : rawValueB;

    return comparePrimitiveValues(valueA, valueB, sort, userLocale);
  }

  return applySortOrder(
    localeCompareNumericLowercase(
      itemA.label,
      itemB.label,
      sortDependencies.userLocale
    ),
    sortDependencies.sort.order
  );
};

/**
 * If there is no component data for both items, we assume that we are sorting non-component groups
 */
export const getRawGraphItemsComparator =
  (
    sortDependencies: ComponentByIdSortDependencies
  ): RawGraphItemsSortComparator =>
  (rawItemA: RawGraphNode2, rawItemB: RawGraphNode2) => {
    const someIdA = rawItemA.modelId || rawItemA.id;
    const someIdb = rawItemB.modelId || rawItemB.id;

    if (
      !sortDependencies.componentAttributesById[someIdA] &&
      !sortDependencies.componentAttributesById[someIdb]
    ) {
      return sortRawGraphItemNonComponentGroups(
        rawItemA,
        rawItemB,
        sortDependencies
      );
    }

    return compareGraphItemsByComponentId({
      componentIdA: rawItemA.modelId,
      componentIdB: rawItemB.modelId,
      sortDependencies,
    });
  };

/**
 * Does not sort non-component groups, use getRawGraphItemsComparator if you need to sort them.
 */
export const getComponentHierarchySortComparator =
  (
    sortDependencies: ComponentByIdSortDependencies
  ): ComponentHierarchySortComparator =>
  ({ modelId: modelIdA }, { modelId: modelIdB }) =>
    compareGraphItemsByComponentId({
      componentIdA: modelIdA,
      componentIdB: modelIdB,
      sortDependencies,
    });

export const extractCollapsedState = (state: TimelineViewModelState) => {
  const {
    timelineViewRenderingData: { collapsedGroupIds, collapsedFieldDataIds },
  } = state;

  const persistedCollapsedState = {
    collapsedGroupIds: Array.from(collapsedGroupIds),
    collapsedFieldDataIds: Array.from(collapsedFieldDataIds),
  };

  return { persistedCollapsedState };
};

export const hasRelevantCollapsedStateUpdate = (
  state: TimelineViewModelState
): boolean => {
  if (state.collapsedStateOrigin !== 'user') {
    return false;
  }

  const hasRenderedItems =
    !!state.timelineViewRenderingData.renderedItemLabels.length;

  const hasNewCollapsedState = !isEqual(
    state.viewSettings.persistedCollapsedState,
    extractCollapsedState(state).persistedCollapsedState
  );

  return hasRenderedItems && hasNewCollapsedState;
};
